import { Mailbox } from "@phosphor-icons/react"
import { FetchError } from "@tokenterminal/core/api"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { TextField } from "@tokenterminal/core/ui/input/TextField"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"

import { useCallback, useReducer } from "react"
import { API_URL } from "@/constants/config"
import MarketingHeading from "./MarketingHeading"

type EmailState = {
  isLoading: boolean
  hasError: boolean
  isDirty: boolean
  error?: string
  success?: string
  email: string
}
// An interface for our actions
type EmailAction =
  | {
      type: "email_update"
      email: string
    }
  | {
      type: "loading"
    }
  | {
      type: "success"
    }
  | {
      type: "error"
      error: string
    }

function emailReducer(state: EmailState, action: EmailAction) {
  switch (action.type) {
    case "email_update": {
      return {
        ...state,
        email: action.email,
        isDirty: true,
        isLoading: false,
        hasError: false,
        success: undefined,
        error: undefined,
      }
    }
    case "loading": {
      return {
        ...state,
        isDirty: false,
        isLoading: true,
        hasError: false,
        success: undefined,
        error: undefined,
      }
    }
    case "success": {
      return {
        ...state,
        success: "Thank you for subscribing!",
        isLoading: false,
      }
    }
    case "error": {
      return {
        ...state,
        isLoading: false,
        hasError: true,
        error: action.error,
      }
    }
  }
}

export function NewsletterSignup() {
  const [{ isLoading, isDirty, hasError, error, success, email }, dispatch] =
    useReducer(emailReducer, {
      email: "",
      isLoading: false,
      hasError: false,
      isDirty: true,
      success: undefined,
      error: undefined,
    })

  const submitHandler = useCallback(async () => {
    dispatch({ type: "loading" })

    if (!email || !email.includes("@") || email.length < 3) {
      dispatch({ type: "error", error: "Invalid email" })
      return
    }

    try {
      await fetch(`${API_URL}/v1/subscribe-to-newsletter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      })

      dispatch({ type: "success" })
      setTimeout(() => {
        dispatch({ type: "email_update", email: "" })
      }, 5000)
    } catch (err) {
      if (err instanceof FetchError) {
        const json = (await err.response.json()) as { message: string }
        dispatch({ type: "error", error: json.message })

        return
      }

      dispatch({ type: "error", error: "An unknown error occurred" })
    }
  }, [email])

  return (
    <div>
      <Card className="p-7 rounded-2xl mt-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4">
            <Card className="size-10 rounded-lg p-0 flex items-center justify-center">
              <Icon as={Mailbox} className="text-fg-secondary size-5" />
            </Card>
            <div className="flex flex-col items-start">
              <MarketingHeading level={4} className="text-lg">
                Subscribe to our weekly newsletter
              </MarketingHeading>
              <div className="text-sm text-fg-secondary">
                Actionable insights you can’t get elsewhere.
              </div>
            </div>
          </div>
          {success ? (
            <div className="text-sm text-fg-emerald">{success}</div>
          ) : (
            <div className="flex gap-2 flex-col md:flex-row items-start relative w-[400px] max-w-full">
              <label className="w-full">
                <div className="sr-only">Email</div>
                <TextField
                  placeholder="Enter your email"
                  isRequired
                  onChange={(value) => {
                    dispatch({ type: "email_update", email: value })
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      submitHandler()
                    }
                  }}
                  autoComplete="off"
                  value={email}
                  style={{ height: "38px", fontSize: "14px" }}
                />
              </label>
              <Button
                onPress={submitHandler}
                isDisabled={!isDirty}
                loading={isLoading}
              >
                Subscribe
              </Button>
              {hasError ? (
                <div
                  className="text-sm text-fg-red"
                  style={{
                    position: "absolute",
                    bottom: "-20px",
                    left: "0",
                  }}
                >
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
